import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import KineticTitle from "@/components/KineticTitle";
import { site, chiSono } from "@/data/content";

export default function ChiSono() {
  return (
    <>
      <Seo
        title={"Chi sono — " + site.name}
        description="La biografia di Luisa Miceli: formazione filosofica, scrittura, libertà femminile, comunicazione digitale, mindfulness e progetti."
      />

      <section className="pt-36 lg:pt-44 pb-16 lg:pb-24" data-testid="chi-sono-hero">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-bordeaux">
              Biografia
            </p>
            <KineticTitle
              lines={["Chi sono,", "oltre le definizioni."]}
              delay={0.25}
              className="mt-6 font-medium text-[clamp(2.5rem,6vw,5rem)] leading-[1.05] tracking-tight"
            />
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-xl text-base sm:text-lg font-light leading-relaxed text-ink2">
                {chiSono.subtitle}
              </p>
            </Reveal>
          </div>
          <Reveal className="lg:col-span-5" delay={0.2}>
            <div className="frame-photo max-w-md lg:ml-auto">
              <img
                src={chiSono.portrait}
                alt={chiSono.portraitAlt}
                className="w-full aspect-[3/4] object-cover"
                data-testid="chi-sono-portrait-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="chi-sono-biografia">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-3">
            <p className="font-mono text-xs tracking-[0.3em] text-bordeaux">La storia</p>
          </Reveal>
          <div className="lg:col-span-8 max-w-3xl space-y-6">
            {chiSono.bio.map((paragraph, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <p
                  className={`leading-relaxed ${
                    i === 0 ? "font-serif text-2xl sm:text-3xl leading-snug" : "text-base font-light text-ink2"
                  }`}
                >
                  {paragraph}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="chi-sono-bio-breve">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="bg-paper2 border-l-2 border-bordeaux p-8 sm:p-12 max-w-3xl">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted3">
                Biografia breve — per presentazioni pubbliche
              </p>
              <p className="mt-5 font-serif italic text-xl sm:text-2xl leading-relaxed">
                {chiSono.shortBio}
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 lg:pb-32" data-testid="chi-sono-cta">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Reveal>
            <div className="bg-dark text-paper p-10 sm:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">
                  Contatti e collaborazioni
                </p>
                <p className="mt-4 font-serif italic text-2xl sm:text-4xl leading-snug max-w-2xl">
                  Se la mia ricerca ti interessa, scrivimi: le idee migliori iniziano da un incontro.
                </p>
              </div>
              <Link
                to="/contatti"
                className="shrink-0 inline-flex items-center gap-3 border border-paper/60 px-8 py-4 text-[11px] uppercase tracking-[0.25em] transition-colors hover:bg-paper hover:text-ink"
                data-testid="chi-sono-contact-cta"
              >
                Contattami
                <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
